import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Star, Phone, MessageSquare, Shield, Clock, ChevronLeft, Eye, Settings } from 'lucide-react';
import toast from 'react-hot-toast';
import { prestataireService, messageService, reviewService } from '../services/api';
import { useAuthStore } from '../store/authStore';

const SERVICES_MAP = {
  MECANIQUE_MOTO: '🏍️ Mécanique Moto', MECANIQUE_VOITURE: '🚗 Mécanique Voiture',
  VULCANISATEUR: '🔩 Vulcanisateur', ELECTRICITE_AUTO: '⚡ Électricité Auto',
  CARROSSERIE: '🎨 Carrosserie', CLIMATISATION: '❄️ Climatisation',
  VIDANGE_ENTRETIEN: '🔧 Vidange & Entretien', PIECES_DETACHEES: '⚙️ Pièces détachées',
};
const PAYMENT_LABELS = {
  MOBILE_MONEY: '📱 Mobile Money', MOOV_MONEY: '🟦 Moov Money',
  CELTIS_CASH: '🟠 Celtis Cash', STRIPE_CARD: '💳 Carte bancaire', ESPECES: '💵 Espèces',
};

export default function ProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [prestataire, setPrestataire] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('about');
  const [msgText, setMsgText] = useState('');
  const [sending, setSending] = useState(false);
  const [userRating, setUserRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const { user, isAuthenticated } = useAuthStore();

  // Est-ce que c'est le prestataire qui regarde son propre profil ?
  const isOwnProfile = isAuthenticated && user?.role === 'PRESTATAIRE' && prestataire?.userId === user?.id;

  useEffect(() => {
    prestataireService.getOne(id)
      .then(r => setPrestataire(r.data))
      .catch(() => toast.error('Profil introuvable.'))
      .finally(() => setLoading(false));
  }, [id]);

  const sendMessage = async () => {
    if (isOwnProfile) return; // bloquer si propre profil
    if (!isAuthenticated) { toast.error('Connectez-vous pour envoyer un message.'); return; }
    if (!msgText.trim()) { toast.error('Message vide.'); return; }
    setSending(true);
    try {
      await messageService.send(prestataire.userId, msgText);
      toast.success('Message envoyé !');
      setMsgText('');
    } catch { toast.error('Erreur envoi message.'); }
    finally { setSending(false); }
  };

  const submitReview = async () => {
    if (isOwnProfile) return;
    if (!isAuthenticated) { toast.error('Connectez-vous pour laisser un avis.'); return; }
    if (!userRating) { toast.error('Choisissez une note.'); return; }
    try {
      await reviewService.add(id, { rating: userRating, comment: reviewComment });
      toast.success('Avis enregistré !');
      setUserRating(0); setReviewComment('');
      const r = await prestataireService.getOne(id);
      setPrestataire(r.data);
    } catch { toast.error('Erreur envoi avis.'); }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', paddingTop: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} style={{ fontSize: '4rem' }}>⚙️</motion.div>
    </div>
  );

  if (!prestataire) return (
    <div style={{ minHeight: '100vh', paddingTop: 120, textAlign: 'center' }}>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '3rem', color: 'var(--blue-dark)' }}>INTROUVABLE</h2>
      <Link to="/prestataires" className="btn btn-primary" style={{ marginTop: '1.5rem', display: 'inline-flex' }}>← Retour</Link>
    </div>
  );

  const initials = `${prestataire.firstName?.[0] || ''}${prestataire.lastName?.[0] || ''}`;
  const stars = (rating, interactive = false) => (
    <div style={{ display: 'flex', gap: 3 }}>
      {[1,2,3,4,5].map(n => (
        <span key={n}
          onClick={interactive && !isOwnProfile ? () => setUserRating(n) : undefined}
          onMouseEnter={interactive && !isOwnProfile ? () => setHoverRating(n) : undefined}
          onMouseLeave={interactive && !isOwnProfile ? () => setHoverRating(0) : undefined}
          style={{
            fontSize: interactive ? '1.8rem' : '1.1rem',
            cursor: interactive && !isOwnProfile ? 'pointer' : 'default',
            color: n <= (interactive ? (hoverRating || userRating) : rating) ? '#FBBF24' : '#D1D5DB',
            transition: 'color 0.15s',
          }}>★</span>
      ))}
    </div>
  );

  // ── BANNIÈRE MODE SPECTATEUR ────────────────────────────
  const SpectatorBanner = () => isOwnProfile ? (
    <motion.div
      initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
      style={{
        background: 'linear-gradient(135deg, #FF6B00, #CC4400)',
        padding: '0.9rem 1.5rem',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '0.8rem',
        position: 'sticky', top: '80px', zIndex: 100,
        boxShadow: '0 4px 20px rgba(255,107,0,0.4)',
      }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', color: 'white' }}>
        <Eye size={18} />
        <span style={{ fontFamily: 'var(--font-cond)', fontWeight: 700, letterSpacing: '1px', fontSize: '0.88rem' }}>
          MODE APERÇU — Vous voyez votre profil comme le voient vos clients
        </span>
      </div>
      <div style={{ display: 'flex', gap: '0.6rem' }}>
        <button onClick={() => navigate('/espace-prestataire')}
          style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', color: 'white', padding: '0.45rem 1rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
          ← Mon tableau de bord
        </button>
        <button onClick={() => navigate('/mon-profil/edit')}
          style={{ background: 'white', border: 'none', color: '#CC4400', padding: '0.45rem 1rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <Settings size={14} /> Modifier mon profil
        </button>
      </div>
    </motion.div>
  ) : null;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--gray-light)', paddingTop: '80px' }}>

      <SpectatorBanner />

      {/* ── BANNER ── */}
      <div style={{ height: 280, background: 'linear-gradient(135deg, var(--blue-dark), #0D1B2A)', position: 'relative', overflow: 'hidden' }}>
        {prestataire.photos?.[0] && (
          <img src={prestataire.photos[0].photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.25 }} />
        )}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(26,59,93,0.9), transparent)' }} />
        <motion.div style={{ position: 'absolute', fontSize: 300, right: '-5%', top: '-10%', color: 'rgba(255,107,0,0.05)', lineHeight: 1 }}
          animate={{ rotate: 360 }} transition={{ duration: 50, repeat: Infinity, ease: 'linear' }}>⚙</motion.div>
        <div className="container" style={{ height: '100%', display: 'flex', alignItems: 'flex-end', paddingBottom: '2rem', position: 'relative', zIndex: 1 }}>
          <Link to={isOwnProfile ? '/espace-prestataire' : '/prestataires'}
            style={{ position: 'absolute', top: '1.5rem', left: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>
            <ChevronLeft size={16} /> {isOwnProfile ? 'Mon tableau de bord' : 'Retour'}
          </Link>
        </div>
      </div>

      <div className="container" style={{ padding: '0 1.5rem 5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '2rem', alignItems: 'start' }} className="profile-grid">

          {/* ── LEFT COLUMN ── */}
          <div>
            {/* Profile header card */}
            <div style={{ background: 'white', borderRadius: 16, padding: '2rem', boxShadow: 'var(--shadow-md)', marginTop: '-80px', position: 'relative', zIndex: 10, border: '1px solid rgba(26,59,93,0.08)' }}>

              {/* Bandeau "votre profil" si prestataire */}
              {isOwnProfile && (
                <div style={{ background: 'rgba(255,107,0,0.06)', border: '1px dashed rgba(255,107,0,0.3)', borderRadius: '10px', padding: '0.6rem 1rem', marginBottom: '1.2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.82rem', color: 'var(--orange)', fontWeight: 600 }}>👁️ Aperçu de votre profil public</span>
                  <button onClick={() => navigate('/mon-profil/edit')}
                    style={{ background: 'var(--orange)', border: 'none', color: 'white', padding: '0.4rem 1rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Settings size={13} /> Modifier
                  </button>
                </div>
              )}

              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                {prestataire.profilePhotoUrl ? (
                  <img src={prestataire.profilePhotoUrl} alt={prestataire.firstName}
                    style={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover', border: '4px solid var(--orange)', boxShadow: '0 8px 25px rgba(255,107,0,0.3)' }} />
                ) : (
                  <div style={{ width: 100, height: 100, borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg, var(--orange), #CC5500)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontFamily: 'var(--font-display)', fontSize: '2.2rem', border: '4px solid white', boxShadow: '0 8px 25px rgba(255,107,0,0.3)' }}>{initials}</div>
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.4rem' }}>
                    {prestataire.isVerified && <span className="badge badge-orange">✅ Vérifié</span>}
                    {prestataire.isAvailable
                      ? <span className="badge badge-green">🟢 Disponible</span>
                      : <span style={{ display: 'inline-flex', alignItems: 'center', padding: '0.25rem 0.7rem', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700, background: 'rgba(239,68,68,0.1)', color: '#EF4444' }}>🔴 Indisponible</span>
                    }
                  </div>
                  <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', color: 'var(--blue-dark)', letterSpacing: '1px', lineHeight: 1, marginBottom: '0.3rem' }}>
                    {prestataire.firstName?.toUpperCase()} {prestataire.lastName?.toUpperCase()}
                  </h1>
                  {prestataire.shopName && (
                    <div style={{ color: 'var(--orange)', fontFamily: 'var(--font-cond)', fontSize: '1rem', fontWeight: 700, marginBottom: '0.5rem' }}>{prestataire.shopName}</div>
                  )}
                  <div style={{ display: 'flex', gap: '1.2rem', flexWrap: 'wrap', fontSize: '0.88rem', color: 'var(--gray-text)' }}>
                    <span><MapPin size={13} style={{ color: 'var(--orange)', verticalAlign: -2 }} /> {prestataire.city}, {prestataire.neighborhood}</span>
                    {prestataire.ratingCount > 0 && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Star size={13} style={{ color: '#FBBF24', fill: '#FBBF24' }} />
                        <strong style={{ color: 'var(--blue-dark)' }}>{prestataire.ratingAvg?.toFixed(1)}</strong>
                        <span>({prestataire.ratingCount} avis)</span>
                      </span>
                    )}
                    {prestataire.experienceYears > 0 && (
                      <span><Clock size={13} style={{ verticalAlign: -2 }} /> {prestataire.experienceYears}+ ans d'expérience</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Action buttons — masqués si propre profil */}
              {!isOwnProfile && (
                <div style={{ display: 'flex', gap: '0.8rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
                  {prestataire.user?.phone && (
                    <a href={`tel:${prestataire.user.phone}`} className="btn btn-primary"><Phone size={16} /> Appeler</a>
                  )}
                  {prestataire.whatsapp && (
                    <a href={`https://wa.me/${prestataire.whatsapp.replace(/\D/g,'')}`} target="_blank" rel="noreferrer"
                      className="btn btn-secondary" style={{ background: '#25D366', borderColor: '#25D366' }}>💬 WhatsApp</a>
                  )}
                  <button className="btn btn-outline" onClick={() => setActiveTab('contact')}>
                    <MessageSquare size={16} /> Envoyer un message
                  </button>
                </div>
              )}

              {/* Si propre profil : bouton modifier à la place */}
              {isOwnProfile && (
                <div style={{ display: 'flex', gap: '0.8rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
                  <button onClick={() => navigate('/mon-profil/edit')} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Settings size={16} /> Modifier mon profil
                  </button>
                  <button onClick={() => navigate('/espace-prestataire')} className="btn btn-outline">
                    ← Retour au tableau de bord
                  </button>
                </div>
              )}
            </div>

            {/* ── TABS ── */}
            <div style={{ display: 'flex', gap: 0, marginTop: '1.5rem', background: 'white', borderRadius: 12, overflow: 'hidden', boxShadow: 'var(--shadow-sm)' }}>
              {[
                { key: 'about',   label: 'À propos' },
                { key: 'photos',  label: `Réalisations (${prestataire.photos?.length || 0})` },
                { key: 'reviews', label: `Avis (${prestataire.ratingCount || 0})` },
                ...(!isOwnProfile ? [{ key: 'contact', label: 'Contacter' }] : []),
              ].map(tab => (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
                  flex: 1, padding: '1rem 0.5rem', border: 'none',
                  background: activeTab === tab.key ? 'var(--blue-dark)' : 'white',
                  color: activeTab === tab.key ? 'white' : 'var(--gray-text)',
                  fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '0.8rem',
                  letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer',
                  transition: 'all 0.2s', borderRight: '1px solid var(--gray-mid)',
                }}>{tab.label}</button>
              ))}
            </div>

            {/* Tab content */}
            <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              style={{ background: 'white', borderRadius: 12, padding: '2rem', marginTop: '0.5rem', boxShadow: 'var(--shadow-sm)', position: 'relative' }}>

              {/* OVERLAY SPECTATEUR sur onglet contact si propre profil */}
              {isOwnProfile && activeTab === 'contact' && (
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(4px)', borderRadius: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, gap: '1rem' }}>
                  <Eye size={40} color="rgba(26,59,93,0.3)" />
                  <p style={{ color: 'var(--gray-text)', textAlign: 'center', maxWidth: '280px', lineHeight: 1.6 }}>
                    Vous ne pouvez pas vous envoyer un message à vous-même.
                  </p>
                </div>
              )}

              {/* ABOUT */}
              {activeTab === 'about' && (
                <div>
                  {prestataire.bio && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <h3 style={sectionH}>À propos</h3>
                      <p style={{ color: 'var(--gray-text)', lineHeight: 1.8 }}>{prestataire.bio}</p>
                    </div>
                  )}
                  <h3 style={sectionH}>Spécialités</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '1.5rem' }}>
                    {prestataire.services?.map(s => (
                      <span key={s.service} style={{ padding: '0.5rem 1rem', background: 'rgba(26,59,93,0.06)', border: '1px solid rgba(26,59,93,0.12)', borderRadius: 8, fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '0.85rem', color: 'var(--blue-dark)' }}>
                        {SERVICES_MAP[s.service] || s.service}
                      </span>
                    ))}
                  </div>
                  {prestataire.address && (
                    <><h3 style={sectionH}>Adresse</h3>
                    <p style={{ color: 'var(--gray-text)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <MapPin size={16} style={{ color: 'var(--orange)' }} />
                      {prestataire.address}, {prestataire.neighborhood}, {prestataire.city}
                    </p></>
                  )}
                </div>
              )}

              {/* PHOTOS */}
              {activeTab === 'photos' && (
                prestataire.photos?.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--gray-text)' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📷</div>
                    <p>Aucune photo de réalisation pour l'instant.</p>
                    {isOwnProfile && (
                      <button onClick={() => navigate('/mon-profil/edit')} className="btn btn-primary" style={{ marginTop: '1rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Settings size={15} /> Ajouter des photos
                      </button>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.8rem' }}>
                    {prestataire.photos?.map(photo => (
                      <motion.div key={photo.id} whileHover={{ scale: 1.04 }} style={{ aspectRatio: '4/3', borderRadius: 10, overflow: 'hidden' }}>
                        <img src={photo.photoUrl} alt={photo.caption || 'Réalisation'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </motion.div>
                    ))}
                  </div>
                )
              )}

              {/* REVIEWS */}
              {activeTab === 'reviews' && (
                <div>
                  {prestataire.ratingCount > 0 && (
                    <div style={{ display: 'flex', gap: '2rem', alignItems: 'center', marginBottom: '2rem', padding: '1.5rem', background: 'var(--gray-light)', borderRadius: 12 }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontFamily: 'var(--font-display)', fontSize: '3.5rem', color: 'var(--orange)', lineHeight: 1 }}>{prestataire.ratingAvg?.toFixed(1)}</div>
                        {stars(prestataire.ratingAvg)}
                        <div style={{ fontSize: '0.8rem', color: 'var(--gray-text)', marginTop: '0.3rem' }}>{prestataire.ratingCount} avis</div>
                      </div>
                    </div>
                  )}

                  {/* Laisser un avis — bloqué si propre profil */}
                  {isAuthenticated && user?.role === 'CLIENT' && !isOwnProfile && (
                    <div style={{ background: 'rgba(255,107,0,0.04)', border: '1px solid rgba(255,107,0,0.2)', borderRadius: 12, padding: '1.5rem', marginBottom: '2rem' }}>
                      <h4 style={{ fontFamily: 'var(--font-cond)', fontSize: '0.8rem', fontWeight: 700, letterSpacing: '2px', color: 'var(--orange)', textTransform: 'uppercase', marginBottom: '1rem' }}>Laisser un avis</h4>
                      {stars(userRating, true)}
                      <textarea className="form-textarea" value={reviewComment} onChange={e => setReviewComment(e.target.value)}
                        placeholder="Partagez votre expérience..." style={{ marginTop: '1rem', minHeight: 80 }} />
                      <button className="btn btn-primary btn-sm" style={{ marginTop: '0.8rem' }} onClick={submitReview}>Publier l'avis</button>
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {prestataire.reviews?.map(r => (
                      <div key={r.id} style={{ padding: '1.2rem', border: '1px solid var(--gray-mid)', borderRadius: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.6rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                          <strong style={{ color: 'var(--blue-dark)', fontFamily: 'var(--font-cond)', fontSize: '1rem' }}>{r.client.firstName} {r.client.lastName}</strong>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                            {stars(r.rating)}
                            <span style={{ fontSize: '0.75rem', color: 'var(--gray-text)', marginLeft: 4 }}>{new Date(r.createdAt).toLocaleDateString('fr-FR')}</span>
                          </div>
                        </div>
                        {r.comment && <p style={{ color: 'var(--gray-text)', fontSize: '0.9rem', lineHeight: 1.6, margin: 0 }}>{r.comment}</p>}
                      </div>
                    ))}
                    {prestataire.reviews?.length === 0 && (
                      <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray-text)' }}>Aucun avis pour l'instant.</div>
                    )}
                  </div>
                </div>
              )}

              {/* CONTACT — uniquement pour non-prestataire */}
              {activeTab === 'contact' && !isOwnProfile && (
                <div>
                  <h3 style={sectionH}>Envoyer un message</h3>
                  {!isAuthenticated ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                      <p style={{ color: 'var(--gray-text)', marginBottom: '1rem' }}>Connectez-vous pour envoyer un message.</p>
                      <Link to="/connexion" className="btn btn-primary">Se connecter</Link>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      <textarea className="form-textarea" value={msgText} onChange={e => setMsgText(e.target.value)}
                        placeholder="Décrivez votre problème, précisez le véhicule, demandez un devis..." style={{ minHeight: 130 }} />
                      <button className="btn btn-primary" onClick={sendMessage} disabled={sending}>
                        {sending ? 'Envoi...' : '📤 Envoyer le message'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </div>

          {/* ── RIGHT SIDEBAR ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem', marginTop: '-40px', position: 'sticky', top: '90px' }}>
            <div style={{ background: 'white', borderRadius: 14, padding: '1.5rem', boxShadow: 'var(--shadow-sm)', border: '1px solid rgba(26,59,93,0.08)' }}>
              <h4 style={cardTitle}>Informations</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
                {[
                  { icon: '📍', label: 'Ville', val: `${prestataire.city}, ${prestataire.neighborhood}` },
                  { icon: '⏰', label: 'Horaires', val: prestataire.availability },
                  { icon: '🛠️', label: 'Expérience', val: `${prestataire.experienceYears}+ ans` },
                  ...(prestataire.priceMin ? [{ icon: '💰', label: 'Tarif min', val: `${prestataire.priceMin?.toLocaleString('fr')} FCFA` }] : []),
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: '0.8rem', alignItems: 'flex-start' }}>
                    <span>{item.icon}</span>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--gray-text)', textTransform: 'uppercase', letterSpacing: '1px', fontFamily: 'var(--font-cond)', fontWeight: 700 }}>{item.label}</div>
                      <div style={{ fontSize: '0.9rem', color: 'var(--blue-dark)', fontWeight: 600 }}>{item.val}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {prestataire.paymentMethods?.length > 0 && (
              <div style={{ background: 'white', borderRadius: 14, padding: '1.5rem', boxShadow: 'var(--shadow-sm)', border: '1px solid rgba(26,59,93,0.08)' }}>
                <h4 style={cardTitle}>Paiements acceptés</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {prestataire.paymentMethods.map(pm => (
                    <div key={pm.method} style={{ padding: '0.6rem 0.9rem', background: 'var(--gray-light)', borderRadius: 8, fontSize: '0.88rem', color: 'var(--blue-dark)', fontWeight: 600 }}>
                      {PAYMENT_LABELS[pm.method] || pm.method}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contact sidebar — masqué si propre profil */}
            {!isOwnProfile && (
              <div style={{ background: 'linear-gradient(135deg, var(--blue-dark), #0D1B2A)', borderRadius: 14, padding: '1.5rem', color: 'white' }}>
                <h4 style={{ fontFamily: 'var(--font-cond)', fontSize: '0.75rem', letterSpacing: '3px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', marginBottom: '1rem' }}>Contact direct</h4>
                {prestataire.user?.phone && (
                  <a href={`tel:${prestataire.user.phone}`} style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', color: 'white', textDecoration: 'none', padding: '0.8rem', background: 'rgba(255,255,255,0.08)', borderRadius: 10, marginBottom: '0.6rem', transition: 'background 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,107,0,0.3)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}>
                    <Phone size={18} style={{ color: 'var(--orange)' }} />
                    <span style={{ fontWeight: 600 }}>{prestataire.user.phone}</span>
                  </a>
                )}
                {prestataire.whatsapp && (
                  <a href={`https://wa.me/${prestataire.whatsapp.replace(/\D/g,'')}`} target="_blank" rel="noreferrer"
                    style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', color: 'white', textDecoration: 'none', padding: '0.8rem', background: 'rgba(37,211,102,0.15)', borderRadius: 10 }}>
                    <span style={{ fontSize: '1.1rem' }}>💬</span>
                    <span style={{ fontWeight: 600 }}>WhatsApp</span>
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <style>{`@media (max-width: 900px) { .profile-grid { grid-template-columns: 1fr !important; } }`}</style>
    </div>
  );
}

const sectionH = { fontFamily: 'var(--font-cond)', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: 'var(--orange)', borderBottom: '2px solid rgba(255,107,0,0.15)', paddingBottom: '0.6rem', marginBottom: '1rem' };
const cardTitle = { fontFamily: 'var(--font-cond)', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: 'var(--orange)', marginBottom: '1.2rem' };
